import { Injectable } from '@angular/core';
import {
  ILuminaria,
  ICreateLuminaria,
  IQueryParam,
  IUpdateLuminaria,
  IAlerta,
  IEstablecimiento,
  ILote,
  IReporteLuminaria,
  IDispositivoLuminaria,
  IDispositivoLuminariaHoratech,
  IReporteLuminariaHoratech,
  IReporteLuminariaHoratechEnergia,
  IConfigDispositivoLuminariaHoratech,
  IDimmerCalendarioConfig,
} from 'modelos/src';
import { BehaviorSubject, Observable } from 'rxjs';
import { apiBase } from 'src/environments/environment';
import { HelperService } from '../../../auxiliares/helper.service';
import { HttpService } from '../../../auxiliares/http.service';
import { DomSanitizer } from '@angular/platform-browser';

const api = apiBase;

// Tipo union para ambas configuraciones de dispositivo
export type IDispositivoLuminariaConfig =
  | IDispositivoLuminaria
  | IDispositivoLuminariaHoratech;

// Tipo union para ambos tipos de reportes
export type IReporteLuminariaConfig =
  | IReporteLuminaria
  | IReporteLuminariaHoratech
  | IReporteLuminariaHoratechEnergia;

export type TipoComandoLuminaria =
  | 'Encender'
  | 'Apagar'
  | 'Reset'
  | 'Reset Energia'
  | 'Pedir GPS'
  | 'Configurar'
  | 'Consultar Config'
  | 'Configurar Calendario'
  | 'Consultar Config Calendario';

export interface IEstadoLuminaria {
  icono?: string;
  ultimoEstado?: string;
  modo?: string;
  alimentacion?: string;
  consumo?: string;
  consumoAcumulado?: string;
  iconoMapa?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LuminariasService {
  public filtrosActivosSubject = new BehaviorSubject<any[]>([]);
  filtrosActivos$ = this.filtrosActivosSubject.asObservable();

  private partidos: IEstablecimiento[] = [];
  private localidades: ILote[] = [];

  constructor(
    private http: HttpService,
    private sanitizer: DomSanitizer,
  ) {}

  // Luminarias
  public getById(id: string): Observable<ILuminaria> {
    return this.http.get(`${api}/luminarias/${id}`);
  }

  public getFiltered(queryParams?: IQueryParam): Observable<{
    data: ILuminaria[];
    total: number;
  }> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`${api}/luminarias`, { params });
  }

  public create(data: ICreateLuminaria): Observable<ILuminaria> {
    return this.http.post(`${api}/luminarias`, data);
  }

  public update(id: string, data: IUpdateLuminaria): Observable<ILuminaria> {
    return this.http.put(`${api}/luminarias/${id}`, data);
  }
  public delete(id: string): Observable<ILuminaria> {
    return this.http.delete(`${api}/luminarias/${id}`);
  }

  // Métodos para actualizar los datos de partidos y localidades
  public actualizarPartidos(partidos: IEstablecimiento[]): void {
    this.partidos = partidos;
  }

  public actualizarLocalidades(localidades: ILote[]): void {
    this.localidades = localidades;
  }

  // Reportes
  public async exportarReportes(
    queryParams?: IQueryParam,
    filename = 'reportes.xlsx',
  ): Promise<void> {
    const params = HelperService.getQueryParams(queryParams);
    await this.http.getFile(
      `${api}/reportes/exportar/luminaria`,
      filename,
      params,
    );
  }

  // Alertas
  public delAlerta(id: string): Observable<IAlerta> {
    return this.http.delete(`${api}/alertas/${id}`);
  }

  // helper
  private readonly FILTROS_STORAGE_KEY = 'luminarias_filtros_activos';

  actualizarFiltros(filtros: string[]): void {
    this.filtrosActivosSubject.next(filtros);
    this.guardarFiltrosEnStorage(filtros);
  }

  private guardarFiltrosEnStorage(filtros: string[]): void {
    try {
      localStorage.setItem(this.FILTROS_STORAGE_KEY, JSON.stringify(filtros));
    } catch (error) {
      console.warn('Error al guardar filtros en localStorage:', error);
    }
  }

  private cargarFiltrosDesdeStorage(): string[] {
    try {
      const filtrosGuardados = localStorage.getItem(this.FILTROS_STORAGE_KEY);
      return filtrosGuardados ? JSON.parse(filtrosGuardados) : [];
    } catch (error) {
      console.warn('Error al cargar filtros desde localStorage:', error);
      return [];
    }
  }

  public inicializarFiltros(): void {
    const filtrosGuardados = this.cargarFiltrosDesdeStorage();
    this.filtrosActivosSubject.next(filtrosGuardados);
  }

  public limpiarTodosLosFiltros(): void {
    this.filtrosActivosSubject.next([]);
    this.guardarFiltrosEnStorage([]);
  }

  estaFiltroActivo(filtro: string): boolean {
    return this.filtrosActivosSubject.getValue().includes(filtro);
  }

  activarFiltro(filtro: string): void {
    const filtros = this.filtrosActivosSubject.getValue();
    if (!filtros.includes(filtro)) {
      const nuevosFiltros = [...filtros, filtro];
      this.filtrosActivosSubject.next(nuevosFiltros);
      this.guardarFiltrosEnStorage(nuevosFiltros);
    }
  }

  desactivarFiltro(filtro: string): void {
    const filtros = this.filtrosActivosSubject.getValue();
    const nuevosFiltros = filtros.filter((f) => f !== filtro);
    this.filtrosActivosSubject.next(nuevosFiltros);
    this.guardarFiltrosEnStorage(nuevosFiltros);
  }

  public getFiltroNombre(filtro: string): string {
    const partido = this.partidos?.find((part) => part._id === filtro);
    const localidad = this.localidades?.find((loc) => loc._id === filtro);

    if (partido) {
      return partido.nombre;
    } else if (localidad) {
      return localidad.nombre;
    } else {
      return filtro;
    }
  }

  private esEstado(filtro: string): boolean {
    // Implementación para determinar si el filtro es un estado
    return [
      'Encendido',
      'Encendido Error',
      'Apagado',
      'Apagado Error',
      'Indeterminado',
    ].includes(filtro);
  }

  private esModo(filtro: string): boolean {
    // Implementación para determinar si el filtro es un modo
    return ['Manual', 'Fotocélula', 'Calendario', 'GPS'].includes(filtro);
  }

  private esPartido(filtro: string): boolean {
    return this.partidos.some((partido) => partido._id === filtro);
  }

  private esLocalidad(filtro: string): boolean {
    return this.localidades.some((localidad) => localidad._id === filtro);
  }

  public getLocalidadesInPartidosFilters(): ILote[] {
    const filtrosActivos = this.filtrosActivosSubject.getValue();
    const filtrosPartido = filtrosActivos.filter((filtro) =>
      this.esPartido(filtro),
    );
    if (filtrosPartido.length === 0) {
      return this.localidades;
    }
    return this.localidades.filter((localidad) =>
      filtrosActivos.includes(localidad.idEstablecimiento),
    );
  }

  public determinarModoLuminaria(luminaria: ILuminaria): string {
    if (!luminaria.dispositivo) {
      return 'Manual';
    }

    const config = luminaria.dispositivo.config;
    if (!config) {
      return 'Manual';
    }

    // Usar configuración normalizada para acceder a campos unificados
    const configNormalizada = this.getConfigNormalizada(config);
    if (configNormalizada.mode) {
      return configNormalizada.mode;
    }

    // Determinar modo basado en campos normalizados
    if (configNormalizada.modoCalendarioActivo) {
      return 'Calendario';
    }

    if (configNormalizada.modoGPSActivo) {
      return 'GPS';
    }

    if (configNormalizada.modoLDRActivo) {
      return 'Fotocélula';
    }

    return 'Manual';
  }

  public determinarEstadoLuminaria(luminaria: ILuminaria): IEstadoLuminaria {
    const estadoLuminaria: IEstadoLuminaria = {
      icono: 'assets/svg/luminarias/indeterminado.svg',
      ultimoEstado: 'Indeterminado',
      alimentacion: 'Consumo normal',
      iconoMapa: 'assets/svg/luminarias/map_indeterminada.webp',
      consumo: '0Kw/h',
      consumoAcumulado: '0 Kw/h',
    };

    if (luminaria.dispositivo === undefined) {
      return estadoLuminaria;
    }

    const config = luminaria.dispositivo.config;
    const reporte = luminaria?.ultimoReporteEnergia?.reporte;
    const reporte2 = luminaria?.ultimoReporte?.reporte;
    const { fechaUltimaComunicacion } = luminaria.dispositivo;

    // Fusionar reportes si hay múltiples disponibles
    const reporteFusionado = this.fusionarReportes(reporte, reporte2);
    const configNormalizada = this.getConfigNormalizada(config);

    // Extraer valores directamente del reporte fusionado
    // Para Horatech: turnOnOffStatus o estadoRele
    const turnOnOffStatus = reporteFusionado.turnOnOffStatus;
    const alarma = configNormalizada.alarma;
    const intervalGeneral = configNormalizada.intervalGeneral;

    // Detectar si es Horatech
    const esHoratech = this.esDispositivoHoratech(luminaria);
    // Consumo/Potencia: Para Horatech usar potencia (W), para otros usar energia (Kw/h)
    let consumo: number;
    let unidadConsumo: string;
    if (esHoratech) {
      // Horatech: usar potencia activa en W
      consumo = reporteFusionado.potencia ?? 0;
      unidadConsumo = ' W';
    } else {
      // Estándar: usar energía en Kw/h
      consumo = reporteFusionado.energia ?? reporteFusionado.activePower ?? 0;
      unidadConsumo = ' Kw/h';
    }
    const consumoAcumulado =
      reporteFusionado.energiaTotal ?? reporteFusionado.activePowerTotal ?? 0;

    // Umbral de tiempo para considerar 2 HORAS
    let umbralTiempo = 2 * 60 * 60 * 1000; // 2 HORAS
    if (intervalGeneral && intervalGeneral > 0) {
      const intervaloX5 = intervalGeneral * 5;
      umbralTiempo = intervaloX5 * 60 * 1000;
    }
    const tiempoActual = Date.now();

    if (
      !fechaUltimaComunicacion ||
      tiempoActual - new Date(fechaUltimaComunicacion).getTime() > umbralTiempo
    ) {
      return estadoLuminaria;
    }
    // Encendida
    if (turnOnOffStatus) {
      if (!alarma || alarma === '') {
        estadoLuminaria.ultimoEstado = 'Encendido';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_encendida.webp';
        estadoLuminaria.icono = 'assets/svg/luminarias/encendida.svg';
        estadoLuminaria.consumo = consumo.toFixed(2) + unidadConsumo;
        estadoLuminaria.consumoAcumulado =
          consumoAcumulado.toFixed(2) + ' Kw/h';
      } else {
        estadoLuminaria.ultimoEstado = 'Encendido Error';
        estadoLuminaria.icono = 'assets/svg/luminarias/encendidaErr.svg';
        estadoLuminaria.iconoMapa =
          'assets/svg/luminarias/map_encendidaErr.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + unidadConsumo;
        estadoLuminaria.consumoAcumulado =
          consumoAcumulado.toFixed(2) + ' Kw/h';
        estadoLuminaria.alimentacion = alarma;
      }
      // Apagada
    } else {
      if (!alarma || alarma === '') {
        estadoLuminaria.ultimoEstado = 'Apagado';
        estadoLuminaria.icono = 'assets/svg/luminarias/apagada.svg';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_apagada.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + unidadConsumo;
        estadoLuminaria.consumoAcumulado =
          consumoAcumulado.toFixed(2) + ' Kw/h';
      } else {
        estadoLuminaria.ultimoEstado = 'Apagado Error';
        estadoLuminaria.icono = 'assets/svg/luminarias/apagadaErr.svg';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_apagadaErr.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + unidadConsumo;
        estadoLuminaria.consumoAcumulado =
          consumoAcumulado.toFixed(2) + ' Kw/h';
        estadoLuminaria.alimentacion = alarma;
      }
    }
    return estadoLuminaria;
  }

  public determinarEstadoLuminariaReportes(
    luminaria: ILuminaria,
    reporte: any,
  ): IEstadoLuminaria {
    const estadoLuminaria: IEstadoLuminaria = {
      icono: 'assets/svg/luminarias/indeterminado.svg',
      ultimoEstado: 'Indeterminado',
      alimentacion: 'Consumo normal',
      iconoMapa: 'assets/svg/luminarias/map_indeterminada.webp',
      consumo: '0W',
      consumoAcumulado: '0 Kw/h',
    };

    if (!luminaria.dispositivo || !reporte || !reporte.reporte) {
      return estadoLuminaria;
    }

    const reporteData = reporte.reporte;

    // Fusionar reportes disponibles
    const reporteFusionado = this.fusionarReportes(reporteData);

    // Extraer valores directamente del reporte fusionado
    const turnOnOffStatus = reporteFusionado.turnOnOffStatus;

    // IMPORTANTE: Usar las alarmas del REPORTE, no de la configuración actual
    // Los reportes Horatech de Estado tienen alarmas?: string[]
    let alarma = '';
    if (reporteFusionado.alarmas && Array.isArray(reporteFusionado.alarmas)) {
      // Horatech: alarmas es un array, unirlas con comas
      alarma =
        reporteFusionado.alarmas.length > 0
          ? reporteFusionado.alarmas.join(', ')
          : false;
    }
    // Los reportes estándar y Horatech de Energía no tienen alarmas históricas

    // Consumo: (Horatech) 'potencia' o 'activePower' (estándar)
    const consumo =
      reporteFusionado.potencia ?? reporteFusionado.activePower ?? 0;
    const consumoAcumulado =
      reporteFusionado.energiaTotal ?? reporteFusionado.activePowerTotal ?? 0;

    // Para los reportes individuales, usamos el estadoRele del reporte si está disponible
    // Si no, usamos la lógica basada en consumo
    const estaEncendida =
      turnOnOffStatus !== undefined && turnOnOffStatus !== null
        ? turnOnOffStatus
        : false;

    // Encendida
    if (estaEncendida) {
      if (!alarma || alarma === '') {
        estadoLuminaria.ultimoEstado = 'Encendido';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_encendida.webp';
        estadoLuminaria.icono = 'assets/svg/luminarias/encendida.svg';
        estadoLuminaria.consumo = consumo.toFixed(2) + ' W';
        estadoLuminaria.consumoAcumulado =
          consumoAcumulado.toFixed(2) + ' Kw/h';
      } else {
        estadoLuminaria.ultimoEstado = 'Encendido Error';
        estadoLuminaria.icono = 'assets/svg/luminarias/encendidaErr.svg';
        estadoLuminaria.iconoMapa =
          'assets/svg/luminarias/map_encendidaErr.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + ' W';
        estadoLuminaria.consumoAcumulado =
          consumoAcumulado.toFixed(2) + ' Kw/h';
        estadoLuminaria.alimentacion = alarma;
      }
    } else {
      // Apagada
      if (!alarma || alarma === '') {
        estadoLuminaria.ultimoEstado = 'Apagado';
        estadoLuminaria.icono = 'assets/svg/luminarias/apagada.svg';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_apagada.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + ' W';
        estadoLuminaria.consumoAcumulado =
          consumoAcumulado.toFixed(2) + ' Kw/h';
      } else {
        estadoLuminaria.ultimoEstado = 'Apagado Error';
        estadoLuminaria.icono = 'assets/svg/luminarias/apagadaErr.svg';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_apagadaErr.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + ' W';
        estadoLuminaria.consumoAcumulado =
          consumoAcumulado.toFixed(2) + ' Kw/h';
        estadoLuminaria.alimentacion = alarma;
      }
    }

    return estadoLuminaria;
  }

  obtenerLuminariasFiltradas(luminarias: ILuminaria[]): ILuminaria[] {
    const filtrosActivos = this.filtrosActivosSubject.getValue();
    if (!luminarias) {
      return [];
    }
    if (filtrosActivos.length === 0) {
      return luminarias;
    }

    // Separar los filtros en estado, modo y grupo
    const filtrosEstado = filtrosActivos.filter((filtro) =>
      this.esEstado(filtro),
    );
    const filtrosModo = filtrosActivos.filter((filtro) => this.esModo(filtro));
    const filtrosPartido = filtrosActivos.filter((filtro) =>
      this.esPartido(filtro),
    );
    const filtrosLocalidad = filtrosActivos.filter((filtro) =>
      this.esLocalidad(filtro),
    );

    return luminarias.filter((luminaria: ILuminaria) => {
      const estado = this.determinarEstadoLuminaria(luminaria).ultimoEstado;
      const modo = this.determinarModoLuminaria(luminaria);
      const idpartidoLuminaria = luminaria.idEstablecimiento;
      const idLocalidadLuminaria = luminaria.idLote;

      if (!estado || !modo) {
        return false;
      }

      // Verificar que la luminaria cumpla con los filtros de estado (inclusivo)
      const cumpleEstado =
        filtrosEstado.length === 0 || filtrosEstado.includes(estado);

      // Verificar que la luminaria cumpla con los filtros de modo (inclusivo)
      const cumpleModo = filtrosModo.length === 0 || filtrosModo.includes(modo);

      // Verificar que la luminaria cumpla con los filtros de grupo (exclusivo)
      let cumplePartido = filtrosPartido.length === 0;
      if (filtrosPartido.length === 1) {
        cumplePartido = idpartidoLuminaria
          ? filtrosPartido.includes(idpartidoLuminaria)
          : false;
      } else if (filtrosPartido.length > 1) {
        cumplePartido = filtrosPartido.some((grupo) =>
          idpartidoLuminaria ? grupo.includes(idpartidoLuminaria) : false,
        );
      } else {
        cumplePartido = true;
      }

      // Verificar que la luminaria cumpla con los filtros de localidad (exclusivo)
      let cumpleLocalidad = filtrosLocalidad.length === 0;
      if (filtrosLocalidad.length === 1) {
        cumpleLocalidad = idLocalidadLuminaria
          ? filtrosLocalidad.includes(idLocalidadLuminaria)
          : false;
      } else if (filtrosLocalidad.length > 1) {
        cumpleLocalidad = filtrosLocalidad.some((localidad) =>
          idLocalidadLuminaria
            ? localidad.includes(idLocalidadLuminaria)
            : false,
        );
      } else {
        cumpleLocalidad = true;
      }

      return cumpleEstado && cumpleModo && cumplePartido && cumpleLocalidad;
    });
  }

  // datos de luminarias
  public getConsumoMaximo(luminaria: ILuminaria): string {
    if (luminaria?.ultimoReporte?.reporte) {
      // Fusionar reportes disponibles
      const reporteFusionado = this.fusionarReportes(
        luminaria.ultimoReporte?.reporte,
        luminaria.ultimoReporteEnergia?.reporte,
      );

      // Consumo:  (Horatech) 'potencia' o 'activePower' (estándar)
      const consumo = reporteFusionado.potencia ?? reporteFusionado.activePower;

      if (consumo !== undefined) {
        return consumo.toFixed(2) + ' W';
      }
    }
    return '0 W';
  }

  // Asegurarse de que las horas tengan el formato correcto con segundos
  public agregarSegundos = (time: string): string => {
    if (time.length === 5) {
      return `${time}:00`;
    }
    return time;
  };

  private convertMsToTime(duration: number): string {
    // const milliseconds = Math.floor((duration % 1000) / 100);
    const seconds = Math.floor((duration / 1000) % 60),
      minutes = Math.floor((duration / (1000 * 60)) % 60),
      hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

    const hoursStr = hours < 10 ? '0' + hours : hours;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const secondsStr = seconds < 10 ? '0' + seconds : seconds;

    return `${hoursStr}:${minutesStr}:${secondsStr}`;
  }

  public getTiempoEncendido(luminaria: ILuminaria): string {
    if (!luminaria.dispositivo?.config) {
      return '00:00:00';
    }

    // Usar configuración normalizada
    const configNormalizada = this.getConfigNormalizada(
      luminaria.dispositivo.config,
    );

    // Para configuración Horatech, no tenemos deviceWorkingTime
    if (configNormalizada.configCalendario) {
      return '00:00:00';
    }

    // Para configuración estándar
    if (configNormalizada.deviceWorkingTime) {
      return this.convertMsToTime(configNormalizada.deviceWorkingTime);
    }

    return '00:00:00';
  }

  public frecReporte(
    deveui: string,
    frecuenciaPeriodico: number,
    frecuenciaEnergia: number,
  ) {
    return this.http.post(`${api}/comandos/luminarias/frecuenciaReporte`, {
      deveui: deveui,
      frecuenciaPeriodico: frecuenciaPeriodico,
      frecuenciaEnergia: frecuenciaEnergia,
    });
  }

  public configHour(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/setearHoraActual`, {
      deveui: deveui,
    });
  }

  /**
   * Determina si un dispositivo es Horatech basándose en su tipo de dispositivo
   */
  public esDispositivoHoratech(luminaria: ILuminaria): boolean {
    if (!luminaria?.dispositivo) {
      return false;
    }
    return luminaria.dispositivo.tipo === 'Luminaria Horatech';
  }

  /**
   * Encender luminaria - detecta automáticamente si es Horatech o estándar
   */
  public encender(deveui: string, dimmer: number, luminaria?: ILuminaria) {
    const endpoint =
      luminaria && this.esDispositivoHoratech(luminaria)
        ? `${api}/comandos/luminarias-horatech/encender`
        : `${api}/comandos/luminarias/encender`;

    return this.http.post(endpoint, {
      deveui: deveui,
      dimmer: dimmer,
    });
  }

  /**
   * Apagar luminaria - detecta automáticamente si es Horatech o estándar
   */
  public apagar(deveui: string, luminaria?: ILuminaria) {
    const endpoint =
      luminaria && this.esDispositivoHoratech(luminaria)
        ? `${api}/comandos/luminarias-horatech/apagar`
        : `${api}/comandos/luminarias/apagar`;

    return this.http.post(endpoint, {
      deveui: deveui,
    });
  }

  /**
   * Dimerizar luminaria - detecta automáticamente si es Horatech o estándar
   */
  public dimerizar(deveui: string, dimmer: number, luminaria?: ILuminaria) {
    const endpoint =
      luminaria && this.esDispositivoHoratech(luminaria)
        ? `${api}/comandos/luminarias-horatech/encender` // Horatech usa encender con dimmer
        : `${api}/comandos/luminarias/dimerizar`;

    return this.http.post(endpoint, {
      deveui: deveui,
      dimmer: dimmer,
    });
  }

  public habilitarGps(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/habilitarGPS`, {
      deveui: deveui,
    });
  }

  public deshabilitarGps(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/deshabilitarGPS`, {
      deveui: deveui,
    });
  }

  public consultGPS(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/consultarGPS`, {
      deveui: deveui,
    });
  }

  public habilitarLDR(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/habilitarModoLDR`, {
      deveui: deveui,
    });
  }

  public deshabilitarLDR(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/deshabilitarModoLDR`, {
      deveui: deveui,
    });
  }

  public habilitarCalendario(deveui: string) {
    return this.http.post(
      `${api}/comandos/luminarias/habilitarModoCalendario`,
      {
        deveui: deveui,
      },
    );
  }

  public deshabilitarCalendario(deveui: string) {
    return this.http.post(
      `${api}/comandos/luminarias/deshabilitarModoCalendario`,
      {
        deveui: deveui,
      },
    );
  }

  public setearOffsetGPS(
    deveui: string,
    offsetAmanecer: number,
    offsetAtardecer: number,
  ) {
    return this.http.post(`${api}/comandos/luminarias/setearOffsetGPS`, {
      deveui: deveui,
      offsetAmanecer: offsetAmanecer,
      offsetAtardecer: offsetAtardecer,
    });
  }

  public setearMargenesLDR(
    deveui: string,
    valorEncendido: number,
    valorApagado: number,
  ) {
    return this.http.post(`${api}/comandos/luminarias/setearMargenesLDR`, {
      deveui: deveui,
      valorEncendido: valorEncendido,
      valorApagado: valorApagado,
    });
  }

  public setearDelayModoLDR(deveui: string, delay: number) {
    return this.http.post(`${api}/comandos/luminarias/setearDelayModoLDR`, {
      deveui: deveui,
      delay: delay,
    });
  }

  public setearEstrategiaModoCalendario(
    deveui: string,
    estrategia: {
      numeroEstrategia: number;
      habilitada: boolean;
      horaDimmer1: string;
      dimmer1: number;
      horaDimmer2: string;
      dimmer2: number;
    },
  ) {
    return this.http.post(
      `${api}/comandos/luminarias/setearEstrategiaModoCalendario`,
      {
        deveui: deveui,
        estrategia: estrategia,
      },
    );
  }

  public setearMargenesAlarmas(
    deveui: string,
    margenes: {
      voltageHighLimit: number;
      voltageLowLimit: number;
      currentHighLimit: number;
      currentLowLimit: number;
    },
  ) {
    return this.http.post(`${api}/comandos/luminarias/setearMargenesAlarmas`, {
      deveui: deveui,
      margenes: margenes,
    });
  }

  public consultarEstrategiaModoCalendario(
    deveui: string,
    numeroEstrategia: number,
  ) {
    return this.http.post(
      `${api}/comandos/luminarias/consultarEstrategiaModoCalendario`,
      {
        deveui: deveui,
        numeroEstrategia: numeroEstrategia,
      },
    );
  }

  public consultarMargenesLDR(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/consultarMargenesLDR`, {
      deveui: deveui,
    });
  }

  // ============================================================================
  // MÉTODOS ESPECÍFICOS PARA LUMINARIAS HORATECH
  // ============================================================================

  /**
   * Crear/actualizar configuración completa de luminaria Horatech
   */
  public createConfigHoratech(
    deveui: string,
    config: IConfigDispositivoLuminariaHoratech,
  ) {
    return this.http.post(`${api}/comandos/luminarias-horatech/createConfig`, {
      deveui: deveui,
      config: config,
    });
  }

  /**
   * Consultar configuración actual de luminaria Horatech
   */
  public consultarConfigHoratech(deveui: string) {
    return this.http.post(
      `${api}/comandos/luminarias-horatech/consultarConfig`,
      {
        deveui: deveui,
      },
    );
  }

  /**
   * Consultar configuración del calendario de luminaria Horatech
   */
  public consultarConfigCalendarioHoratech(deveui: string) {
    return this.http.post(
      `${api}/comandos/luminarias-horatech/consultarConfigCalendario`,
      {
        deveui: deveui,
      },
    );
  }

  /**
   * Crear/actualizar configuración del calendario de luminaria Horatech
   */
  public createConfigCalendarioHoratech(
    deveui: string,
    config: IDimmerCalendarioConfig,
  ) {
    return this.http.post(
      `${api}/comandos/luminarias-horatech/createConfigCalendario`,
      {
        deveui: deveui,
        config: config,
      },
    );
  }

  /**
   * Pedir GPS para luminaria Horatech
   */
  public pedirGPSHoratech(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias-horatech/pedirgps`, {
      deveui: deveui,
    });
  }

  /**
   * Resetear luminaria Horatech
   */
  public resetHoratech(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias-horatech/reset`, {
      deveui: deveui,
    });
  }

  /**
   * Resetear energía de luminaria Horatech
   */
  public resetEnergiaHoratech(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias-horatech/resetEnergia`, {
      deveui: deveui,
    });
  }

  /**
   * Sincronizar hora del dispositivo Horatech
   */
  public setTimestampHoratech(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias-horatech/setTimestamp`, {
      deveui: deveui,
    });
  }

  /**
   * Comando genérico para luminarias Horatech
   */
  public comandoGenericoHoratech(
    ids: string[],
    tipoComando: TipoComandoLuminaria,
    dato?: {
      dimmer?: number;
      config?: IConfigDispositivoLuminariaHoratech;
      configCalendario?: IDimmerCalendarioConfig;
    },
  ) {
    return this.http.post(
      `${api}/comandos/luminarias-horatech/comandoGenerico`,
      {
        ids: ids,
        tipoComando: tipoComando,
        dato: dato,
      },
    );
  }

  // ============================================================================
  // FIN MÉTODOS ESPECÍFICOS HORATECH
  // ============================================================================

  // MÉTODOS AUXILIARES PARA DETECCIÓN AUTOMÁTICA
  // ============================================================================
  /**
   * Helper para determinar si es configuración Horatech
   */
  public esConfiguracionHoratech(config: any): boolean {
    return config && 'configCalendario' in config;
  }

  /**
   * Helper para extraer valores de configuración según el tipo
   */
  public extraerValorConfig(
    config: any,
    propiedadEstandar: string,
    propiedadHoratech?: string,
  ): any {
    if (this.esConfiguracionHoratech(config)) {
      return propiedadHoratech
        ? config[propiedadHoratech]
        : config[propiedadEstandar];
    }
    return config[propiedadEstandar];
  }

  /**
   * Helper para obtener modo GPS activo
   */
  public getModoGPSActivo(config: any): boolean {
    if (!config) return false;

    if (this.esConfiguracionHoratech(config)) {
      // Para Horatech, GPS está activo si tiene offsets GPS configurados
      return (
        (config.offsetGPSAmanecer !== undefined &&
          config.offsetGPSAmanecer !== null) ||
        (config.offsetGPSAtardecer !== undefined &&
          config.offsetGPSAtardecer !== null)
      );
    } else {
      // Configuración estándar
      return config.modoGPSActivo || false;
    }
  }

  /**
   * Helper para obtener modo LDR activo
   */
  public getModoLDRActivo(config: any): boolean {
    if (!config) return false;

    if (this.esConfiguracionHoratech(config)) {
      // Para Horatech, LDR está activo si tiene límites de luz configurados
      return (
        (config.limLuzInferior !== undefined &&
          config.limLuzInferior !== null) ||
        (config.limLuzSuperior !== undefined && config.limLuzSuperior !== null)
      );
    } else {
      // Configuración estándar
      return config.modoLDRActivo || false;
    }
  }

  /**
   * Helper para obtener modo calendario activo
   */
  public getModoCalendarioActivo(config: any): boolean {
    if (!config) return false;

    if (this.esConfiguracionHoratech(config)) {
      // Para Horatech, calendario está activo si configCalendario.calendarioHabilitado es true
      return config.configCalendario?.calendarioHabilitado || false;
    } else {
      // Configuración estándar
      return config.modoCalendarioActivo || false;
    }
  }

  /**
   * Helper para obtener offset amanecer
   */
  public getOffsetAmanecer(config: any): number | null {
    if (!config) return null;

    if (this.esConfiguracionHoratech(config)) {
      return config.offsetGPSAmanecer;
    } else {
      return config.offsetAmanecer;
    }
  }

  /**
   * Helper para obtener offset atardecer
   */
  public getOffsetAtardecer(config: any): number | null {
    if (!config) return null;

    if (this.esConfiguracionHoratech(config)) {
      return config.offsetGPSAtardecer;
    } else {
      return config.offsetAtardecer;
    }
  }

  /**
   * Helper para obtener valor encendido LDR
   */
  public getValorEncendido(config: any): number | null {
    if (!config) return null;

    if (this.esConfiguracionHoratech(config)) {
      return config.limLuzSuperior;
    } else {
      return config.valorEncendido;
    }
  }

  /**
   * Helper para obtener valor apagado LDR
   */
  public getValorApagado(config: any): number | null {
    if (!config) return null;

    if (this.esConfiguracionHoratech(config)) {
      return config.limLuzInferior;
    } else {
      return config.valorApagado;
    }
  }

  /**
   * Helper para obtener delay LDR
   */
  public getDelayLDR(config: any): number | null {
    if (!config) return null;

    if (this.esConfiguracionHoratech(config)) {
      return config.delayLDR || null;
    } else {
      return config.delayLDR;
    }
  }

  /**
   * Helper para obtener frecuencia general
   */
  public getIntervalGeneral(config: any): number | null {
    if (!config) return null;

    if (this.esConfiguracionHoratech(config)) {
      return config.frecReporte;
    } else {
      return config.intervalGeneral;
    }
  }

  /**
   * Helper para obtener frecuencia energia
   */
  public getIntervalEnergy(config: any): number | null {
    if (!config) return null;

    if (this.esConfiguracionHoratech(config)) {
      return config.frecReporteEnergia || config.frecReporte;
    } else {
      return config.intervalEnergy;
    }
  }

  /**
   * Helper para obtener estrategias según el tipo de configuración
   */
  public getEstrategias(config: any): any[] {
    if (this.esConfiguracionHoratech(config)) {
      // Para Horatech, las estrategias están en configCalendario.puntos
      return config?.configCalendario?.puntos || [];
    } else {
      // Configuración estándar
      return [
        config?.estrategia1,
        config?.estrategia2,
        config?.estrategia3,
      ].filter((estrategia) => estrategia);
    }
  }

  // Helper público para normalizar distintas configuraciones de dispositivo (estándar / Horatech)
  // Mantiene TODOS los campos originales + agrega campos normalizados
  public getConfigNormalizada(config: any): any {
    if (!config) return {};

    // Crear una copia de la configuración original para mantener todos los campos
    const configNormalizada = { ...config };

    // Agregar campos normalizados que funcionen para ambos tipos
    configNormalizada.modoGPSActivo = this.getModoGPSActivo(config);
    configNormalizada.modoLDRActivo = this.getModoLDRActivo(config);
    configNormalizada.modoCalendarioActivo =
      this.getModoCalendarioActivo(config);
    configNormalizada.offsetAmanecer = this.getOffsetAmanecer(config);
    configNormalizada.offsetAtardecer = this.getOffsetAtardecer(config);
    configNormalizada.valorEncendido = this.getValorEncendido(config);
    configNormalizada.valorApagado = this.getValorApagado(config);
    configNormalizada.delayLDR = this.getDelayLDR(config);
    configNormalizada.intervalGeneral = this.getIntervalGeneral(config);
    configNormalizada.intervalEnergy = this.getIntervalEnergy(config);
    configNormalizada.estrategias = this.getEstrategias(config);

    return configNormalizada;
  }

  // Helper público para fusionar múltiples reportes en uno solo
  // NO normaliza campos, solo combina todos los reportes manteniendo nombres originales
  // Útil para tener todos los datos disponibles (Estado + Energía juntos)
  public fusionarReportes(...reportes: any[]): any {
    // Filtrar reportes válidos
    const reportesValidos = reportes.filter((r) => r != null);

    if (reportesValidos.length === 0) return {};

    // Fusionar todos los reportes en uno solo (último gana en caso de conflicto)
    const reporteFusionado = Object.assign({}, ...reportesValidos);

    return reporteFusionado;
  }

  // Alias para compatibilidad - simplemente fusiona reportes sin normalizar
  public getReporteNormalizado(...reportes: any[]): any {
    return this.fusionarReportes(...reportes);
  }
}
